"use client";

/**
 * BalanceReviewScreen — host for the Decide › Balance tab.
 *
 * 2026-05-06 redesign: replaced the legacy stack of
 *   - "Area emphasis" (Light / Meaningful / Core toggles per area)
 *   - "Life allocation" (LifeAllocationCard sliders)
 *   - "Allocation gaps" (per-area pace vs scheduled tile grid)
 *   - "Needs protection" / "Over-absorbing" callout cards
 *   - The PetalLifeWheel + per-area card grid
 *
 * with a single `<BalanceList>` (Variant A "Paired" from the design
 * handoff at /tmp/tin_handoff/design_handoff_balance_review). The list
 * shows committed vs scheduled per area in one horizontal bar; the empty
 * tail of the bar IS the gap. Sort is gap-descending; null targets sink.
 *
 * Adjust commitments is now a modal overlay (`<AdjustCommitments>`) that
 * replaces the old inline `BalanceEditMode` grid.
 *
 * Deprecated components preserved for archive (no longer rendered here):
 *   - PetalLifeWheel, MiniLifeWheel (still used by ReflectScreen)
 *   - LifeAllocationCard
 *   - BalanceEditMode
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import AdjustCommitments, {
  type AdjustCommitmentsAreaPatch,
} from "@/components/balance/AdjustCommitments";
import BalanceList, {
  areaKeyToIcon,
  type BalanceListArea,
} from "@/components/balance/BalanceList";
import {
  getLifeAreas,
  type LifeAreaItem,
} from "@/lib/api";
import { patchLifeAreasBatch } from "@/lib/api/balance";
import { getReflectProjection } from "@/lib/api/reflect";
import type { ReflectAllocationArea } from "@/lib/api/projection-types";
import { invalidateProjectionStores } from "@/lib/stores/projectionInvalidation";

interface Props {
  onNavigate: (tab: string, initialMessage?: string) => void;
}

export default function BalanceReviewScreen({ onNavigate: _onNavigate }: Props) {
  const [areas, setAreas] = useState<LifeAreaItem[]>([]);
  const [reflectAreas, setReflectAreas] = useState<ReflectAllocationArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lifeResponse, reflectProjection] = await Promise.all([
        getLifeAreas(),
        getReflectProjection().catch(() => null),
      ]);
      setAreas(lifeResponse.areas);
      setReflectAreas(reflectProjection?.allocation?.areas ?? []);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Could not load balance review.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const balanceListAreas = useMemo<BalanceListArea[]>(() => {
    const reflectByArea = new Map(
      reflectAreas.map((area) => [area.area, area]),
    );
    return areas.map((life) => {
      const reflect = reflectByArea.get(life.area) ?? null;
      const target = life.target_minutes_per_week;
      const scheduled = reflect?.scheduled_minutes ?? 0;
      const primaryGoal = reflect?.primary_goal ?? null;
      return {
        id: life.area,
        name: life.label,
        icon: areaKeyToIcon(life.area),
        target_minutes_per_week: target,
        scheduled_minutes_this_week: scheduled,
        primary_goal: primaryGoal
          ? {
              id: primaryGoal.id,
              title: primaryGoal.title,
              progress: primaryGoal.progress_pct,
            }
          : null,
      };
    });
  }, [areas, reflectAreas]);

  const handleSave = useCallback(
    async (changed: AdjustCommitmentsAreaPatch[]): Promise<void> => {
      // The host is the source of truth for the LifeArea row id (uuid)
      // while the modal works in `area.area` (e.g. "career") space — map
      // back to ids before calling the batch endpoint.
      const idByArea = new Map(areas.map((a) => [a.area, a.id]));
      const payload = changed
        .map((c) => {
          const id = idByArea.get(c.id);
          if (!id) return null;
          return { id, target_minutes_per_week: c.target_minutes_per_week };
        })
        .filter((x): x is { id: string; target_minutes_per_week: number | null } => x !== null);
      if (payload.length === 0) return;
      const updated = await patchLifeAreasBatch(payload);
      setAreas(updated.areas);
      invalidateProjectionStores(["today", "plan", "reflect", "assistant"]);
      void loadData();
    },
    [areas, loadData],
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-72 animate-pulse rounded-[14px] border border-border bg-card" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Decide sub-tab header — pattern matches BacklogScreen ("Library")
          so the four sub-tabs visually align. Previously this screen
          rendered the BalanceList directly with no top-of-page anchor,
          making it the only Decide sub-tab without a heading. */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
            Decisionease · Balance
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-forest">Balance</h2>
          <p className="mt-1 text-sm text-muted">
            Where your time is going across life areas, and what to adjust.
          </p>
        </div>
      </div>
      {error ? (
        <div className="rounded-[14px] border border-amber/30 bg-amber/5 px-4 py-3 text-sm text-earth">
          {error}
        </div>
      ) : null}
      <BalanceList
        areas={balanceListAreas}
        onAdjustClick={() => setAdjustOpen(true)}
      />
      {adjustOpen && (
        <AdjustCommitments
          areas={balanceListAreas}
          onSave={handleSave}
          onClose={() => setAdjustOpen(false)}
        />
      )}
    </div>
  );
}
